import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { SEO } from "@/components/seo/SEO";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Users, Lightbulb, Shield, TrendingUp, HandHeart, Zap } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const PilotProjecten = () => {
  const [formData, setFormData] = useState({
    bedrijfsnaam: "",
    contactpersoon: "",
    email: "",
    telefoon: "",
    postcode: "",
    bedrijfstype: "",
    ervaring_jaren: "",
    specialisatie: "",
    interesse_projecten: [] as string[],
    bericht: "",
    akkoord_privacy: false,
    akkoord_samenwerking: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field: string, value: string | boolean | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleInteresseChange = (project: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      interesse_projecten: checked
        ? [...prev.interesse_projecten, project]
        : prev.interesse_projecten.filter(p => p !== project)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { error } = await supabase.functions.invoke('send-contact-email', {
        body: {
          type: 'pilot_project',
          bedrijfsnaam: formData.bedrijfsnaam,
          contactpersoon: formData.contactpersoon,
          email: formData.email,
          telefoon: formData.telefoon,
          postcode: formData.postcode,
          bedrijfstype: formData.bedrijfstype,
          ervaring_jaren: formData.ervaring_jaren,
          specialisatie: formData.specialisatie,
          interesse_projecten: formData.interesse_projecten.join(', '),
          bericht: formData.bericht,
          akkoord_privacy: formData.akkoord_privacy,
          akkoord_samenwerking: formData.akkoord_samenwerking
        }
      });

      if (error) throw error;

      toast.success("Bedankt voor je interesse! We nemen binnen 2 werkdagen contact met je op.");
      setFormData({
        bedrijfsnaam: "",
        contactpersoon: "",
        email: "",
        telefoon: "",
        postcode: "",
        bedrijfstype: "",
        ervaring_jaren: "",
        specialisatie: "",
        interesse_projecten: [],
        bericht: "",
        akkoord_privacy: false,
        akkoord_samenwerking: false
      });
    } catch (error) {
      console.error('Error:', error);
      toast.error("Er ging iets mis. Probeer het opnieuw of bel ons direct.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const pilotProjecten = [
    {
      titel: "Coöperatie Snellaadparking Pannerden",
      beschrijving: "Energie & Mobiliteitshub met zonnecarport, batterijopslag en coöperatief verdienmodel voor lokale ondernemers",
      status: "Actief",
      locatie: "Pannerden (Zevenaar)", 
      icon: <Zap className="h-6 w-6" />,
      link: "/coop-snellaadparking"
    },
    {
      titel: "Energy Hub Coöperatie",
      beschrijving: "Lokale energiegemeenschappen waar kleinere installateurs een centrale rol spelen",
      status: "Actief",
      locatie: "Gelderland",
      icon: <Users className="h-6 w-6" />
    },
    {
      titel: "Innovatieve Financiering",
      beschrijving: "Nieuwe financieringsmodellen die lagere drempels en betere marges garanderen",
      status: "Ontwikkeling",
      locatie: "Landelijk",
      icon: <TrendingUp className="h-6 w-6" />
    },
    {
      titel: "Smart Grid Integratie",
      beschrijving: "Geavanceerde monitoring en sturing voor optimale energieverdeling",
      status: "Pilot",
      locatie: "Elst e.o.",
      icon: <Zap className="h-6 w-6" />
    },
    {
      titel: "Kennis & Training Hub",
      beschrijving: "Gezamenlijke opleidingen en certificeringen voor toekomstbestendige vaardigheden",
      status: "Planning",
      locatie: "Regionaal",
      icon: <Lightbulb className="h-6 w-6" />
    }
  ];

  const voordelen = [
    {
      titel: "Gegarandeerde Projecten",
      beschrijving: "Stabiele werkvoorraad door coöperatieve opdrachtverdeling",
      icon: <Shield className="h-5 w-5" />
    },
    {
      titel: "Betere Marges",
      beschrijving: "Gezamenlijke inkoop en efficiënte projectuitvoering",
      icon: <TrendingUp className="h-5 w-5" />
    },
    {
      titel: "Kennis Delen",
      beschrijving: "Leer van elkaar in plaats van tegen elkaar concurreren",
      icon: <Users className="h-5 w-5" />
    },
    {
      titel: "Innovatie Toegang",
      beschrijving: "Toegang tot nieuwste technologieën en financieringsmodellen",
      icon: <Lightbulb className="h-5 w-5" />
    }
  ];

  return (
    <>
      <SEO 
        title="Pilot Projecten - Samenwerking in Duurzame Energie | Sol-Vi"
        description="Doe mee aan innovatieve pilot projecten in Gelderland. Wij geloven in samenwerking in plaats van concurrentie voor een sterkere duurzame energie sector."
        keywords="pilot projecten, samenwerking installateurs, energie coöperatie, innovatieve financiering, Gelderland, kleinere installateurs, duurzame energie samenwerking"
        image="/assets/hero-solar.jpg"
      />
      
      <Header />
      
      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-primary/5 to-secondary/5 py-20">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto">
              <Badge variant="outline" className="mb-4">
                <HandHeart className="h-4 w-4 mr-2" />
                Pilot Fase
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">
                Samen Innoveren in 
                <span className="text-primary"> Duurzame Energie</span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                Wij geloven in <strong>samenwerking in plaats van concurrentie</strong>. 
                Onze pilot projecten zijn erop gericht om kleinere installateurs 
                zekerheid en verdiensten te garanderen in de groeiende energiesector.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="text-lg px-8" asChild>
                  <a href="#aanmelden">Doe Mee aan Pilot</a>
                </Button>
                <Button variant="outline" size="lg" className="text-lg px-8" asChild>
                  <a href="#projecten">Bekijk Projecten</a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Onze Filosofie */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Waarom Samenwerking?
              </h2>
              <p className="text-lg text-muted-foreground">
                De energietransitie vraagt om een nieuwe aanpak. In plaats van elkaar te beconcurreren, 
                bundelen we krachten om samen sterker te staan.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {voordelen.map((voordeel, index) => (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <div className="text-primary">
                        {voordeel.icon}
                      </div>
                    </div>
                    <CardTitle className="text-xl">{voordeel.titel}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{voordeel.beschrijving}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Pilot Projecten */}
        <section id="projecten" className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Innovatieve Projecten in Ontwikkeling
              </h2>
              <p className="text-lg text-muted-foreground">
                Ontdek de pionierprojecten waar je als installateur deel van kunt uitmaken
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {pilotProjecten.map((project, index) => (
                <Card key={index} className="hover:shadow-lg transition-all">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="text-primary">
                          {project.icon}
                        </div>
                        <div>
                          <CardTitle className="text-xl mb-1">{project.titel}</CardTitle>
                          <div className="flex items-center gap-2">
                            <Badge variant={
                              project.status === 'Actief' ? 'default' :
                              project.status === 'Pilot' ? 'secondary' : 'outline'
                            }>
                              {project.status}
                            </Badge>
                            <span className="text-sm text-muted-foreground">{project.locatie}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{project.beschrijving}</p>
                    {project.link && (
                      <Button className="mt-4 w-full" asChild>
                        <a href={project.link}>Bekijk project</a>
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Aanmelden Sectie */}
        <section id="aanmelden" className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Geloof Jij in Coöperatie?
                </h2>
                <p className="text-lg text-muted-foreground">
                  Laat je gegevens achter en we nemen contact met je op voor een kennismakingsgesprek 
                  over de mogelijkheden binnen onze pilot projecten.
                </p>
              </div>

              <Card className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="bedrijfsnaam">Bedrijfsnaam *</Label>
                      <Input
                        id="bedrijfsnaam"
                        required
                        value={formData.bedrijfsnaam}
                        onChange={(e) => handleInputChange('bedrijfsnaam', e.target.value)}
                        placeholder="Jouw bedrijfsnaam"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contactpersoon">Contactpersoon *</Label>
                      <Input
                        id="contactpersoon"
                        required
                        value={formData.contactpersoon}
                        onChange={(e) => handleInputChange('contactpersoon', e.target.value)}
                        placeholder="Voor- en achternaam"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="email">E-mailadres *</Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="je@bedrijf.nl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="telefoon">Telefoonnummer *</Label>
                      <Input
                        id="telefoon"
                        type="tel"
                        required
                        value={formData.telefoon}
                        onChange={(e) => handleInputChange('telefoon', e.target.value)}
                        placeholder="06 12345678"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="postcode">Postcode *</Label>
                      <Input
                        id="postcode"
                        required
                        value={formData.postcode}
                        onChange={(e) => handleInputChange('postcode', e.target.value.toUpperCase())}
                        placeholder="1234AB"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="bedrijfstype">Bedrijfstype *</Label>
                      <Select 
                        value={formData.bedrijfstype} 
                        onValueChange={(value) => handleInputChange('bedrijfstype', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecteer type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="installateur">Installateur</SelectItem>
                          <SelectItem value="elektricien">Elektricien</SelectItem>
                          <SelectItem value="dakdekker">Dakdekker</SelectItem>
                          <SelectItem value="energieadviseur">Energieadviseur</SelectItem>
                          <SelectItem value="projectontwikkelaar">Projectontwikkelaar</SelectItem>
                          <SelectItem value="anders">Anders</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ervaring_jaren">Jaren ervaring</Label>
                      <Select 
                        value={formData.ervaring_jaren} 
                        onValueChange={(value) => handleInputChange('ervaring_jaren', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecteer" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0-2">0-2 jaar</SelectItem>
                          <SelectItem value="3-5">3-5 jaar</SelectItem>
                          <SelectItem value="6-10">6-10 jaar</SelectItem>
                          <SelectItem value="10+">10+ jaar</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="specialisatie">Specialisatie</Label>
                    <Input
                      id="specialisatie"
                      value={formData.specialisatie}
                      onChange={(e) => handleInputChange('specialisatie', e.target.value)}
                      placeholder="Bijvoorbeeld: zonnepanelen, warmtepompen, thuisbatterijen..."
                    />
                  </div>

                  <div className="space-y-4">
                    <Label>Interesse in pilot projecten:</Label>
                    <div className="grid md:grid-cols-2 gap-4">
                      {pilotProjecten.map((project) => (
                        <div key={project.titel} className="flex items-center space-x-2">
                          <Checkbox
                            id={project.titel}
                            checked={formData.interesse_projecten.includes(project.titel)}
                            onCheckedChange={(checked) => 
                              handleInteresseChange(project.titel, checked as boolean)
                            }
                          />
                          <Label 
                            htmlFor={project.titel}
                            className="text-sm font-normal cursor-pointer"
                          >
                            {project.titel}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bericht">Bericht (optioneel)</Label>
                    <Textarea
                      id="bericht"
                      value={formData.bericht}
                      onChange={(e) => handleInputChange('bericht', e.target.value)}
                      placeholder="Vertel ons meer over je interesse in samenwerking..."
                      rows={4}
                    />
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-start space-x-2">
                      <Checkbox
                        id="privacy"
                        checked={formData.akkoord_privacy}
                        onCheckedChange={(checked) => 
                          handleInputChange('akkoord_privacy', checked as boolean)
                        }
                        required
                      />
                      <Label 
                        htmlFor="privacy"
                        className="text-sm font-normal cursor-pointer"
                      >
                        Ik ga akkoord met het verwerken van mijn gegevens volgens het privacybeleid *
                      </Label>
                    </div>
                    <div className="flex items-start space-x-2">
                      <Checkbox
                        id="samenwerking"
                        checked={formData.akkoord_samenwerking}
                        onCheckedChange={(checked) => 
                          handleInputChange('akkoord_samenwerking', checked as boolean)
                        }
                        required
                      />
                      <Label 
                        htmlFor="samenwerking"
                        className="text-sm font-normal cursor-pointer"
                      >
                        Ik geloof in samenwerking in plaats van concurrentie en wil meer weten over de pilot projecten *
                      </Label>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full text-lg" 
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Versturen...' : 'Meld Je Aan voor Pilot'}
                  </Button>
                </form>
              </Card>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default PilotProjecten;